import styled from 'styled-components';
import React, { useContext } from 'react';
import produce from 'immer';
import { useComputed, useSignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { TEMP_NO_SELECTOR } from '@/features/editor/widgets/changelog/placeholder';
import { LocationPicker } from '@/features/editor/widgets/shared/location-picker';
import { createNewElementMessageFromOrigChange } from '@/features/editor/context/use-transpiler';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  AiOriginAction,
  AllMutationKind,
  CompoundChange,
  HtmlMutationKind,
} from '@/pkg/sdk';
import { DeviceType } from '@/utils/definitions';
import { AiEditorTabContainer } from '@/features/editor/widgets/shared/ai-editing-layout/tab-container';
import {
  AiComponentContextProps,
  AiComponentContextProvider,
} from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { VSpace } from '@/components/spacing';
import { SelectedAgent } from '@/features/ai-agents/shared/models';
import { getStartersWithCards } from '@/features/editor/widgets/ai-widget-builder/use-cases/starters-with-cards';

export interface AiComponentWidgetProps {
  originalChange: EditorDeclarativeBlock;
}

export function AiComponentWidget({ originalChange }: AiComponentWidgetProps) {
  useSignals();
  const {
    devicePreview: {
      editorState: { device },
    },
    inspectorNav: { gotoAppCatalog },
  } = useContext(EditorContext);

  const newChange = useSignal(originalChange);

  const onBackFromPicker = () => {
    const newChange = produce(originalChange, (draft) => {
      draft.block.selector = TEMP_NO_SELECTOR;
    });
    gotoAppCatalog(createNewElementMessageFromOrigChange(newChange));
  };

  const onSelectorPicked = (
    selector: string,
    kind: AllMutationKind,
    isInCart?: boolean,
  ) => {
    newChange.value = produce(newChange.value, (draft) => {
      draft.block.selector = selector;
      draft.editorSelector = selector;
      const block = draft?.block?.value as CompoundChange;
      if (block?.htmlKind) {
        block.htmlKind = kind as HtmlMutationKind;
      }
      draft.isInCart = isInCart;
    });
  };

  const props = useComputed(
    () =>
      ({
        originalChange: newChange.value,
        selectedAgent: {
          id: `widget-developer`,
          type: `agent`,
        } as SelectedAgent,
        starters: getStartersWithCards,
        originAction: AiOriginAction.WIDGET_BUILDER,
        placeholder: `Tell us what you want to build`,
        onEditAttachment: (attachmentsSignal, oldAttachment, newSelector) => {
          console.debug(`noop onEditAttachment`, {
            attachmentsSignal,
            oldAttachment,
            newSelector,
          });
        },
      } as AiComponentContextProps),
  );

  const hasNoPlacement = useComputed(
    () => newChange?.value?.block?.selector === TEMP_NO_SELECTOR,
  );

  return (
    <>
      {hasNoPlacement.value && (
        <LocationPicker
          onContinue={onSelectorPicked}
          onBack={onBackFromPicker}
          title="Choose a location for the new section"
          backCaption="< Back to widgets"
        />
      )}
      {!hasNoPlacement.value && (
        <Wrapper device={device}>
          <VSpace value={2} />

          <AiComponentContextProvider
            key="ai-component-ctx-provider"
            props={props}
          >
            <AiEditorTabContainer />
          </AiComponentContextProvider>
          <VSpace value={2} />
        </Wrapper>
      )}
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `100%`};

  display: flex;
  flex-direction: column;
`;
